import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { DashboardGrid } from "@/components/DashboardGrid";
import { CardFormModal } from "@/components/CardFormModal";
import { Footer } from "@/components/Footer";
import { listCards, createCard, updateCard, deleteCard } from "@/lib/api";

function App() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, card: null });

  const load = useCallback(async () => {
    try {
      const data = await listCards();
      setCards(data);
    } catch (e) {
      console.error("Failed to load cards", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const scrollTo = (target) => {
    if (window.__lenis) {
      window.__lenis.scrollTo(target, { offset: target === "#top" ? 0 : -60 });
    } else if (target === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const openAdd = () => setModal({ open: true, card: null });
  const openEdit = (card) => setModal({ open: true, card });

  const handleSave = async (values, id) => {
    try {
      if (id) {
        await updateCard(id, values);
        toast.success("Card updated");
      } else {
        await createCard(values);
        toast.success("Dashboard added to the deck");
      }
      setModal({ open: false, card: null });
      await load();
    } catch (e) {
      toast.error("Could not save the card");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCard(id);
      toast.success("Card deleted");
      setModal({ open: false, card: null });
      await load();
    } catch (e) {
      toast.error("Could not delete the card");
    }
  };

  const platforms = new Set(cards.map((c) => c.type)).size;

  return (
    <div id="top" className="min-h-screen bg-paper text-ink">
      <Navbar onAdd={openAdd} onBrowse={scrollTo} />
      <main>
        <Hero
          cardsCount={cards.length}
          platforms={platforms}
          loading={loading}
          onAdd={openAdd}
          onBrowse={() => scrollTo("#deck")}
        />
        <DashboardGrid
          cards={cards}
          loading={loading}
          onAdd={openAdd}
          onEdit={openEdit}
        />
      </main>
      <Footer />
      <CardFormModal
        open={modal.open}
        card={modal.card}
        onSave={handleSave}
        onDelete={handleDelete}
        onClose={() => setModal({ open: false, card: null })}
      />
      <Toaster position="bottom-right" theme="light" richColors />
    </div>
  );
}

export default App;
